import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Leaf, Home as HomeIcon, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO, { orgSchema, websiteSchema, localBusinessSchema } from "@/components/SEO";
import { trackClick } from "@/lib/analytics";
import { PRODUCTS } from "@/data/products";
import { useProducts, getCategoryLabel } from "@/lib/useProducts";
import { KITS } from "@/data/kits";

const TRUST = [
  { icon: Sparkles, title: "Pure & Authentic", text: "Sourced and crafted to traditional standards." },
  { icon: Leaf, title: "Traditional Quality", text: "Premium grade ingredients, never diluted." },
  { icon: HomeIcon, title: "Made For Every Home", text: "Modern packaging, timeless tradition." },
  { icon: ShieldCheck, title: "Trusted In Every Ritual", text: "Consistency you can build a household on." },
];

const WHY = [
  { title: "Premium Packaging", text: "Designed to sit gracefully on the modern Indian shelf." },
  { title: "Authentic Ingredients", text: "Sourced from heritage suppliers across India." },
  { title: "Competitive Pricing", text: "Premium quality, fair to every household." },
  { title: "Trusted Ritual Solutions", text: "Hand-curated for every prayer, every paath." },
  { title: "Retailer Friendly", text: "Strong margins, fast moving, easy reorder." },
  { title: "Made In India", text: "Crafted with pride. Distributed with care." },
];

const Home = () => {
  const { products } = useProducts();
  const featured = products.slice(0, 8);

  return (
    <div data-testid="home-page">
      <SEO
        title="Sanatan Dharma™ — Buy Authentic Puja Essentials Online | Har Ghar Ka Vishwas"
        description="India's trusted brand for premium ritual essentials — kapoor, kumkum, kalava, sambrani, agarbatti, gangajal, havan samagri and complete puja kits. Authentic, quality-tested, pan-India delivery. Har Ghar Ka Vishwas."
        keywords="sanatan dharma, sanatan dharma online, sanatandharmaonline, buy puja essentials online, buy kapoor online, buy kumkum, buy kalava, buy sambrani cups, buy gangajal online, buy agarbatti, buy dhoop, buy havan samagri, hanuman puja kit, lakshmi puja kit, shivji puja kit, griha pravesh kit, har ghar ka vishwas, indian ritual products, premium puja brand"
        path="/"
        jsonLd={[orgSchema, websiteSchema, localBusinessSchema]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden mandala-bg" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="overline mb-6" data-testid="hero-overline">Har Ghar Ka Vishwas — Since Always</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-[#3B2A1A]" data-testid="hero-headline">
              Rooted In Tradition.<br/>
              <span className="italic font-light text-[#C89D3C]">Made For Every Home.</span>
            </h1>
            <p className="mt-7 text-lg lg:text-xl leading-relaxed text-[#5C4A3A] font-light max-w-xl">
              Pure ritual essentials crafted with authenticity, quality and trust — for every prayer, every ritual and every Indian household.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/products" className="btn-primary" data-testid="hero-explore-btn" onClick={() => trackClick("hero-explore-products")}>
                Explore Products <ArrowRight size={14} />
              </Link>
              <Link to="/retailer" className="btn-outline" data-testid="hero-retailer-btn" onClick={() => trackClick("hero-become-retail-partner")}>
                Become Retail Partner <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-sm text-[#8A7969]">
              <div data-testid="hero-stat-1">
                <p className="font-serif-display text-3xl text-[#3B2A1A]">24</p>
                <p className="uppercase tracking-widest text-[10px] mt-1">Sacred Essentials</p>
              </div>
              <div className="w-px h-10 bg-[#3B2A1A]/15" />
              <div data-testid="hero-stat-2">
                <p className="font-serif-display text-3xl text-[#3B2A1A]">5</p>
                <p className="uppercase tracking-widest text-[10px] mt-1">Curated Kits</p>
              </div>
              <div className="w-px h-10 bg-[#3B2A1A]/15" />
              <div data-testid="hero-stat-3">
                <p className="font-serif-display text-3xl text-[#3B2A1A]">100%</p>
                <p className="uppercase tracking-widest text-[10px] mt-1">Made In India</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up delay-2 relative">
            <div className="relative aspect-square flex items-center justify-center">
              <img
                src="/brand/logo.png"
                alt="Sanatan Dharma — Har Ghar Ka Vishwas"
                className="w-full h-full object-contain"
                style={{ mixBlendMode: "multiply" }}
                data-testid="hero-image"
              />
            </div>
            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-[#FBF6EC] border border-[#3B2A1A]/15 px-6 py-4 items-center gap-4 max-w-xs">
              <div className="w-10 h-10 rounded-full bg-[#C89D3C]/15 flex items-center justify-center">
                <Sparkles size={16} className="text-[#C89D3C]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#8A7969]">Crafted with</p>
                <p className="font-serif-display text-lg leading-tight text-[#3B2A1A]">Heritage Care</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST PILLARS */}
      <section className="border-y border-[#3B2A1A]/10 bg-[#F5EFE3]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t, i) => (
            <div key={t.title} data-testid={`trust-pillar-${i}`}
              className={`py-10 lg:py-14 px-4 lg:px-8 ${i !== 0 ? "lg:border-l border-[#3B2A1A]/10" : ""} ${i % 2 !== 0 ? "border-l lg:border-l border-[#3B2A1A]/10" : ""}`}>
              <t.icon size={22} className="text-[#C89D3C]" />
              <h3 className="font-serif-display text-2xl mt-5 leading-tight text-[#3B2A1A]">{t.title}</h3>
              <p className="text-sm text-[#5C4A3A] mt-2 font-light leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT RANGE */}
      <section className="py-24 lg:py-32" data-testid="product-range-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <SectionHeading overline="Our Product Range" title="The Everyday Essentials, Reimagined." subtitle="From kapoor to kalawa, every product is held to the same uncompromising ratio of tradition, quality and craft." testId="product-range-heading" />
            <Link to="/products" className="text-sm uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] hover-underline flex items-center gap-2 self-start" data-testid="view-all-products-link">
              View Full Range <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#3B2A1A]/10 border border-[#3B2A1A]/10">
            {featured.map((p) => (
              <Link
                to={`/products/${p.slug}`}
                key={p.slug}
                data-testid={`featured-product-${p.slug}`}
                className="group bg-white p-5 sm:p-7 flex flex-col hover:bg-[#FBF6EC]/40 transition-colors"
              >
                <div className="aspect-square overflow-hidden bg-[#F5EFE3] mb-5">
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
                <p className="overline text-[10px]">{getCategoryLabel(p.category)}</p>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-2 leading-tight">{p.name}</h3>
                <p className="text-sm text-[#8A7969] mt-1 font-light">{p.tagline}</p>
                <span className="mt-5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C89D3C] flex items-center gap-2">Explore <ArrowRight size={12} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED KITS */}
      <section className="py-24 lg:py-32 bg-[#F5EFE3] border-y border-[#3B2A1A]/10" data-testid="kits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Special Occasion Kits" title="Distinct Colours. Same Trust." subtitle="Hand-curated kits for the rituals that anchor every Indian household — Hanuman, Shani, Shiv, Navratri and Griha Pravesh." testId="kits-heading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {KITS.map((k, i) => (
              <Link
                to="/kits"
                key={k.slug}
                data-testid={`featured-kit-${k.slug}`}
                className={`relative min-h-[360px] p-8 lg:p-10 text-white flex flex-col justify-between overflow-hidden group ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}
                style={{ background: k.color }}
              >
                <div className="absolute inset-0 mandala-bg opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="relative">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">Puja Kit</p>
                  <h3 className="font-serif-display text-4xl lg:text-5xl mt-3 leading-tight">{k.name}</h3>
                  <p className="text-sm text-white/85 mt-3 max-w-xs">{k.tagline}</p>
                </div>
                <div className="relative flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold">
                  Explore Kit <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 lg:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden border border-[#3B2A1A]/15">
              <img
                src="https://images.unsplash.com/photo-1763610642272-787bfeb8045c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="A row of warm diyas in an Indian home"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionHeading overline="The Essence" title="The Essence Of Sanatan Dharma" subtitle="Sanatan Dharma was created to bring purity, authenticity and trust back to everyday rituals. Our products are thoughtfully curated for modern households while remaining rooted in timeless Indian traditions." />
            <div className="mt-10 grid grid-cols-2 gap-x-10 gap-y-8 max-w-md">
              <div data-testid="essence-stat-1">
                <p className="font-serif-display text-4xl text-[#C89D3C]">∞</p>
                <p className="text-sm text-[#5C4A3A] mt-2 font-light">Generations of tradition, distilled.</p>
              </div>
              <div data-testid="essence-stat-2">
                <p className="font-serif-display text-4xl text-[#C89D3C]">1</p>
                <p className="text-sm text-[#5C4A3A] mt-2 font-light">Trusted standard for every product.</p>
              </div>
            </div>
            <Link to="/about" className="btn-outline mt-10" data-testid="about-cta">Our Story <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 lg:py-32 bg-[#3B2A1A] text-[#FBF6EC]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="overline text-[#E2C37B] mb-4">Why Choose Us</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">A standard, held everywhere.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FBF6EC]/10 mt-14 border border-[#FBF6EC]/10">
            {WHY.map((w, i) => (
              <div key={w.title} data-testid={`why-card-${i}`} className="bg-[#3B2A1A] p-8 lg:p-10">
                <p className="font-serif-display text-3xl text-[#E2C37B]">0{i + 1}</p>
                <h3 className="font-serif-display text-2xl mt-5">{w.title}</h3>
                <p className="text-sm text-[#FBF6EC]/75 mt-3 font-light leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDIAMART CTA */}
      <section className="py-16 lg:py-20 bg-[#F5EFE3] border-y border-[#3B2A1A]/10" data-testid="indiamart-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-4">Now Available On</p>
              <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight text-[#3B2A1A]">
                Order Directly From Our <span className="italic text-[#C89D3C] font-light">IndiaMART Store.</span>
              </h2>
              <p className="text-base lg:text-lg text-[#5C4A3A] mt-5 font-light leading-relaxed max-w-2xl">
                Sanatan Dharma — a unit of Sara Greenworks Pvt. Ltd. — is a verified supplier on IndiaMART. Order any product in bulk, request wholesale rates, and get direct trade support from our team.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="https://www.indiamart.com/saragreenworksprivatelimited-newdelhi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("home-indiamart-cta")}
                  data-testid="indiamart-cta-btn"
                  className="btn-primary"
                >
                  Visit Our IndiaMART Store <ArrowUpRight size={14} />
                </a>
                <a
                  href="https://www.indiamart.com/saragreenworksprivatelimited-newdelhi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("home-indiamart-wholesale")}
                  className="btn-outline"
                >
                  Request Wholesale Quote <ArrowRight size={14} />
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-px bg-[#3B2A1A]/10 border border-[#3B2A1A]/10">
                <div className="bg-white p-6" data-testid="indiamart-stat-verified">
                  <p className="font-serif-display text-3xl text-[#C89D3C]">✓</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-3 leading-tight">Verified Supplier</p>
                  <p className="text-xs text-[#5C4A3A] mt-1 font-light">TrustSEAL certified on IndiaMART</p>
                </div>
                <div className="bg-white p-6" data-testid="indiamart-stat-orders">
                  <p className="font-serif-display text-3xl text-[#C89D3C]">B2B</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-3 leading-tight">Wholesale Ready</p>
                  <p className="text-xs text-[#5C4A3A] mt-1 font-light">Bulk quotes for retailers & distributors</p>
                </div>
                <div className="bg-white p-6" data-testid="indiamart-stat-delivery">
                  <p className="font-serif-display text-3xl text-[#C89D3C]">IN</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-3 leading-tight">Pan-India Delivery</p>
                  <p className="text-xs text-[#5C4A3A] mt-1 font-light">Every state, every city</p>
                </div>
                <div className="bg-white p-6" data-testid="indiamart-stat-support">
                  <p className="font-serif-display text-3xl text-[#C89D3C]">1:1</p>
                  <p className="font-serif-display text-lg text-[#3B2A1A] mt-3 leading-tight">Direct Trade Support</p>
                  <p className="text-xs text-[#5C4A3A] mt-1 font-light">Personal reply within 48 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RETAIL PARTNERS CTA */}
      <section className="py-24 lg:py-32" data-testid="retail-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading overline="Retail Partners" title="Grow With Sanatan Dharma." subtitle="Become a retail partner and bring trusted ritual essentials to households across India. Premium shelf presence, attractive margins, fast-moving products." />
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-y-3 max-w-xl">
              {["Premium Shelf Presence", "Attractive Margins", "Retail Support", "Fast Moving Products", "Distributor Opportunities"].map((b) => (
                <li key={b} className="text-sm text-[#5C4A3A] flex items-start gap-3" data-testid={`retail-benefit-${b}`}>
                  <span className="w-1.5 h-1.5 bg-[#C89D3C] mt-2 rounded-full" />
                  {b}
                </li>
              ))}
            </ul>
            <Link to="/retailer" className="btn-primary mt-10" data-testid="retail-cta-btn" onClick={() => trackClick("home-become-partner")}>Become Partner <ArrowRight size={14} /></Link>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden border border-[#3B2A1A]/15">
              <img src="https://images.unsplash.com/photo-1644061925268-053b6a592c2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Premium retail shelf with traditional Indian items" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
